import type { Accept } from 'react-dropzone';

export enum FileDropzoneStatus {
  Uploading = 'uploading',
  Uploaded = 'uploaded',
}

export type FileDropzoneItem = {
  /** File shown in the list under the dropzone. */
  file: File;
  /** Visual upload state for the file row. */
  status: FileDropzoneStatus;
  /** Determinate upload progress from 0 to 100. Ignored unless status is `FileDropzoneStatus.Uploading`. */
  progress?: number;
};

type FileDropzoneLabels = {
  /** Label for the browse action inside the drop area. */
  browse: string;
  /** Status label shown next to a successfully added file. */
  uploaded: string;
  /** Accessible label for the remove-file action. */
  remove: string;
  /** Accessible label for the cancel-upload action. */
  cancel: string;
  /** Accessible label for the download-file action. */
  download: string;
};

export type FileDropzoneProps = {
  /** Files currently associated with the dropzone. */
  files: FileDropzoneItem[];
  /** Called with the files accepted from drop or browse. */
  onFilesSelected: (files: File[]) => void;
  /** Called when the user removes an uploaded file. */
  onRemoveFile: (file: File) => void;
  /** Called when the user cancels an in-progress upload. */
  onCancelUpload?: (file: File) => void;
  /** Called whenever the dropzone's validity changes: `false` when a dropped file is rejected, `true` once a valid file is accepted. */
  onValidityChange?: (isValid: boolean) => void;
  /** Primary instruction shown inside the drop area. */
  title: string;
  /** Secondary constraint text, e.g. accepted types and size. */
  hint: string;
  /** User-facing labels for browse, status, and file actions. */
  labels: FileDropzoneLabels;
  /** Message shown when a dropped file is rejected. */
  invalidFileMessage: string;
  /** Accepted MIME types and extensions. */
  accept?: Accept;
  /** Maximum file size in bytes. */
  maxSize?: number;
  /** When false, only the first accepted file is kept. */
  multiple?: boolean;
  /** Disables drop, browse, and file actions. */
  disabled?: boolean;
  /**
   * Test id applied to the drop area. Child elements append a suffix:
   * - `{data-testid}` — drop area
   * - `{data-testid}-input` — hidden file input; defaults to `file-dropzone-input` when omitted
   * - `{data-testid}-file-list` — file list; defaults to `file-dropzone-file-list` when omitted
   */
  'data-testid'?: string;
  hasError?: boolean;
};

export type FileDropzoneFileItemProps = {
  item: FileDropzoneItem;
  labels: FileDropzoneLabels;
  disabled?: boolean;
  onRemoveFile: (file: File) => void;
  onCancelUpload?: (file: File) => void;
};
